import React, { useRef, useState } from "react";
import { SlidersHorizontal, Star } from "lucide-react";
import { useIntl } from "react-intl";
import Popover from "../../../../../../library/UI/Popover";
import ChartTabs from "../ChartTabs";
import IndicatorCatalog from "./IndicatorCatalog";
import CurrentIndicators from "./CurrentIndicators";
import useFitChartMenu from "../../hooks/useFitChartMenu";
import "./IndicatorsMenu.sass";

const TRIGGER_ICON_SIZE = 18;
const TAB_ICON_SIZE = 14;
const TAB_FAVORITES = "favorites";
const TAB_CURRENT = "current";
const TAB_ALL = "all";

/**
 * Indicators toolbar button with the count badge and its menu
 * (po-chart-visual-spec.md §5.1): the Favorites / Current / All tabs, as in
 * PO — Favorites while an indicator is starred, Current while one is added,
 * «Delete all (N)» (not in Favorites). The menu stays open after adding.
 *
 * @param {object}   props
 * @param {object[]} props.indicators - engine.getIndicators() items (useIndicators)
 * @param {(id: string) => void} props.onAdd
 * @param {(key: string) => void} props.onEdit - opens the settings of an indicator
 * @param {(key: string) => void} props.onRemove
 * @param {() => void} props.onRemoveAll
 * @param {string[]} props.favoriteIds
 * @param {(id: string) => void} props.onToggleFavorite
 * @param {boolean}  props.isOpen
 * @param {() => void} props.onToggle
 * @param {() => void} props.onClose
 */
const IndicatorsMenu = ({
  indicators,
  onAdd,
  onEdit,
  onRemove,
  onRemoveAll,
  favoriteIds,
  onToggleFavorite,
  isOpen,
  onToggle,
  onClose,
}) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [tab, setTab] = useState(TAB_ALL);
  const menuRef = useRef(null);
  const menuStyle = useFitChartMenu(menuRef, isOpen);

  const count = indicators.length;
  const tabs = [
    favoriteIds.length > 0 && {
      id: TAB_FAVORITES,
      label: t("futures.chart.indicators.tab_favorites"),
      icon: <Star size={TAB_ICON_SIZE} fill="currentColor" aria-hidden="true" />,
    },
    count > 0 && { id: TAB_CURRENT, label: t("futures.chart.indicators.tab_current") },
    { id: TAB_ALL, label: t("futures.chart.indicators.tab_all") },
  ].filter(Boolean);
  // A tab that emptied (the last star or indicator removed) falls back to All
  const activeTab = tabs.some(({ id }) => id === tab) ? tab : TAB_ALL;
  const label = t("futures.chart.toolbar.indicators");

  const trigger = (
    <button
      type="button"
      className={`chart-toolbar__btn${isOpen ? " chart-toolbar__btn--active" : ""}`}
      aria-label={label}
      title={label}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <span className="chart-toolbar__btn-icon">
        <SlidersHorizontal size={TRIGGER_ICON_SIZE} aria-hidden="true" />
      </span>
      {count > 0 && (
        <span className="chart-toolbar__badge" aria-hidden="true">
          {count}
        </span>
      )}
    </button>
  );

  return (
    <Popover isOpen={isOpen} onClose={onClose} trigger={trigger}>
      <div className="indicators-menu" ref={menuRef} style={menuStyle}>
        {tabs.length > 1 && <ChartTabs tabs={tabs} activeId={activeTab} onSelect={setTab} />}
        <div className="indicators-menu__list">
          {activeTab === TAB_CURRENT ? (
            <CurrentIndicators indicators={indicators} onEdit={onEdit} onRemove={onRemove} />
          ) : (
            <IndicatorCatalog
              favoriteIds={favoriteIds}
              isFavoritesOnly={activeTab === TAB_FAVORITES}
              onAdd={onAdd}
              onToggleFavorite={onToggleFavorite}
            />
          )}
        </div>
        {count > 0 && activeTab !== TAB_FAVORITES && (
          <button type="button" className="indicators-menu__delete-all" onClick={onRemoveAll}>
            {t("futures.chart.indicators.delete_all", { count })}
          </button>
        )}
      </div>
    </Popover>
  );
};

export default IndicatorsMenu;
